import React from 'react';
import {
  Checkbox as AriaCheckbox,
  CheckboxProps as AriaCheckboxProps,
} from 'react-aria-components';
import clsx from 'clsx';
import { colors } from '../../theme';
import styles from './CustomCheckbox.module.css';

export interface CustomCheckboxProps extends Omit<AriaCheckboxProps, 'children'> {
  /**
   * Checkbox label
   */
  label?: string;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Indeterminate state
   */
  isIndeterminate?: boolean;
  /**
   * No interaction mode (disables pointer events and removes gap)
   */
  noInteraction?: boolean;
}

/**
 * CustomCheckbox component - an accessible checkbox using react-aria-components
 */
export const CustomCheckbox: React.FC<CustomCheckboxProps> = ({
  label,
  className = '',
  isIndeterminate = false,
  isDisabled = false,
  noInteraction = false,
  ...props
}) => {
  return (
    <AriaCheckbox
      className={clsx(
        styles.wrapper,
        { [styles.wrapperNoInteraction]: noInteraction },
        className
      )}
      isDisabled={isDisabled}
      isIndeterminate={isIndeterminate}
      {...props}
    >
      {({ isSelected, isIndeterminate: isIndeterminateState, isFocusVisible }) => (
        <>
          <div className={styles.checkboxContainer}>
            <div
              className={clsx(
                styles.checkbox,
                {
                  [styles.checkboxChecked]: isSelected || isIndeterminateState,
                  [styles.checkboxDisabled]: isDisabled,
                  [styles.checkboxFocused]: isFocusVisible,
                }
              )}
            >
              {isSelected && !isIndeterminateState && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className={clsx(styles.icon, { [styles.iconDisabled]: isDisabled })}
                >
                  <path
                    d="M2 6L5 9L10 3"
                    stroke={isDisabled ? colors.gray[400] : colors.background.primary}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {isIndeterminateState && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className={clsx(styles.icon, { [styles.iconDisabled]: isDisabled })}
                >
                  <path
                    d="M3 6H9"
                    stroke={isDisabled ? colors.gray[400] : colors.background.primary}
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </div>
          </div>
          {label && (
            <span
              className={clsx(styles.label, { [styles.labelDisabled]: isDisabled })}
            >
              {label}
            </span>
          )}
        </>
      )}
    </AriaCheckbox>
  );
};

CustomCheckbox.displayName = 'ProdiktCustomCheckbox';
